import { useMemo, useState } from "react";
import { Package, SearchX } from "lucide-react";
import ProductCard from "./ProductCard";
import StorefrontFilters from "./StorefrontFilters";
import { primaryTabLabelFor } from "./StorefrontTabs";

/**
 * StorefrontProductGrid — responsive grid with search + category filter.
 * Layout:
 *   mobile   grid-cols-2
 *   sm/md    grid-cols-3
 *   lg       grid-cols-4
 *   xl       grid-cols-5
 * Cards are compact enough that ~4 products fit in one mobile viewport (2 rows).
 */
export function StorefrontProductGrid({ business, products, onAddToCart }) {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All");
  const primaryLabel = primaryTabLabelFor(business);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.category && set.add(p.category));
    return ["All", ...Array.from(set)];
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      if (activeCat !== "All" && p.category !== activeCat) return false;
      if (!q) return true;
      return (
        (p.name || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    });
  }, [products, query, activeCat]);

  if (!products.length) {
    return (
      <div
        className="rounded-2xl border border-dashed border-tbc-gold/40 bg-tbc-cream/40 p-10 text-center"
        data-testid="storefront-grid-empty"
      >
        <Package className="w-10 h-10 mx-auto text-tbc-gold" strokeWidth={1.3} />
        <p className="mt-3 font-heading text-lg text-tbc-navy">
          No {primaryLabel.toLowerCase()} yet
        </p>
        <p className="mt-1 text-sm text-gray-500 font-body max-w-md mx-auto">
          The owner hasn't listed anything here. Check back soon — new drops
          happen every week on The Blk Community.
        </p>
      </div>
    );
  }

  return (
    <div>
      <StorefrontFilters
        primaryLabel={primaryLabel}
        query={query}
        onQueryChange={setQuery}
        categories={categories}
        activeCategory={activeCat}
        onCategoryChange={setActiveCat}
      />

      {filtered.length === 0 ? (
        <div
          className="rounded-2xl border border-gray-100 bg-white p-10 text-center"
          data-testid="storefront-grid-no-match"
        >
          <SearchX className="w-8 h-8 mx-auto text-gray-400" strokeWidth={1.4} />
          <p className="mt-3 font-heading text-tbc-navy">No matches</p>
          <p className="mt-1 text-sm text-gray-500 font-body">
            Try a different search or clear the category filter.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5"
          data-testid="storefront-grid"
        >
          {filtered.map((p) => (
            <ProductCard key={p.product_id} product={p} onAddToCart={onAddToCart} />
          ))}
        </div>
      )}
    </div>
  );
}

export default StorefrontProductGrid;
